import React, { useEffect, useState } from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { exportToExcel, exportToPDF } from '../../utils/export';
import { formatMoney, fmtDate, unwrapList } from '../../utils/format';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import Loader from '../../components/common/Loader';
import SearchableSelect from '../../components/common/SearchableSelect';
import { showError } from '../../utils/alert';
import { COMPANY_NAME } from '../../config';

export default function Ledger() {
  const [accounts, setAccounts] = useState([]);
  const [filters, setFilters] = useState({ account_id: '', from: '', to: '' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.accounts.list({ per_page: 500, operation: 'view-ledger' }).then(({ data: d }) => {
      setAccounts(unwrapList(d, 'accounts'));
    }).catch(() => {});
  }, []);

  const load = async () => {
    if (!filters.account_id) {
      showError('Please select an account');
      return;
    }
    setLoading(true);
    try {
      const params = { account_id: filters.account_id, from: filters.from || undefined, to: filters.to || undefined };
      const { data: res } = await api.reports.ledger(params);
      setData(res);
    } catch (err) {
      showError('Failed to load ledger', extractError(err));
    } finally {
      setLoading(false);
    }
  };

  const rows = data ? (data.entries && data.entries.data ? data.entries.data : data.entries) || [] : [];
  const exportRows = [...rows].reverse();
  const exportSummary = data ? {
    opening_balance: data.opening_balance || 0,
    total_debit: data.totals?.debit || 0,
    total_credit: data.totals?.credit || 0,
    closing_balance: data.closing_balance || 0,
  } : null;
  const cols = [
    { key: 'date', label: 'Date', render: (r) => fmtDate(r.date) },
    { key: 'voucher_no', label: 'Voucher No' },
    { key: 'voucher_type', label: 'Voucher Type' },
    { key: 'narration', label: 'Narration', render: (r) => (r.narration ? String(r.narration).toUpperCase() : '-') },
    { key: 'debit', label: 'Debit', render: (r) => Number(r.debit) ? formatMoney(r.debit) : '' },
    { key: 'credit', label: 'Credit', render: (r) => Number(r.credit) ? formatMoney(r.credit) : '' },
    { key: 'balance', label: 'Balance', render: (r) => formatMoney(r.balance) },
  ];
  const exportCols = cols.filter((c) => c.key !== 'voucher_type' && c.key !== 'voucher_no');
  const party = data?.party || null;
  const mainHeading = party && !party.ntn_no && !party.gst_no ? 'FM' : COMPANY_NAME;

  return (
    <div className="page">
      <PageHeader
        title="Ledger"
        subtitle="Account-wise ledger with running balance."
        crumbs={[{ label: 'Reports', to: '/reports' }, { label: 'Ledger' }]}
        actions={
          <>
            <button className="btn btn-secondary" onClick={() => exportToPDF(exportRows, 'ledger_report', exportCols, { from: filters.from, to: filters.to }, mainHeading, exportSummary, data.account?.name)} disabled={!rows.length}><span>PDF</span></button>
            <button className="btn btn-success" onClick={() => exportToExcel(exportRows, 'ledger_report', exportCols, { from: filters.from, to: filters.to }, mainHeading, exportSummary, data.account?.name)} disabled={!rows.length}><span>Export Excel</span></button>
          </>
        }
      />

      <div className="card">
        <div className="card-body">
          <div className="filters-bar">
            <SearchableSelect options={accounts} value={filters.account_id} onChange={(v) => setFilters({ ...filters, account_id: v })} placeholder="-- Select account --" getLabel={(a) => a.name} />
            <input className="form-control" type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
            <input className="form-control" type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} />
            <button className="btn btn-primary btn-sm" onClick={load}><FiRefreshCw /> Run</button>
          </div>

          {data && (
            <div className="stats-grid" style={{ marginBottom: 18 }}>
              <div className="stat-card"><div className="stat-icon indigo"><i className="bi bi-cash-stack" /></div><div><div className="stat-value">{formatMoney(data.opening_balance)}</div><div className="stat-label">Opening Balance</div></div></div>
              <div className="stat-card"><div className="stat-icon green"><i className="bi bi-box-arrow-in-down" /></div><div><div className="stat-value">{formatMoney(data.totals?.debit)}</div><div className="stat-label">Total Debit</div></div></div>
              <div className="stat-card"><div className="stat-icon orange"><i className="bi bi-box-arrow-up" /></div><div><div className="stat-value">{formatMoney(data.totals?.credit)}</div><div className="stat-label">Total Credit</div></div></div>
              <div className="stat-card"><div className="stat-icon blue"><i className="bi bi-flag-fill" /></div><div><div className="stat-value">{formatMoney(data.closing_balance)}</div><div className="stat-label">Closing Balance</div></div></div>
            </div>
          )}

          {loading ? <Loader text="Loading ledger..." /> : (
            <DataTable data={rows} columns={cols} emptyTitle="Select an account to view ledger" />
          )}
        </div>
      </div>
    </div>
  );
}